<i18n lang="yaml" locale="en">
  standard: Standard
  justified: Gallery mode
  column: "no columns | one column | {no} columns"
  row: "No rows"
  auto: auto
  small: small
  medium: medium
  large: large
  off: off
  every n rows: "every {no} rows"
  full width: Fill width
  full height: Fill height
  horizontal left: horizontal left
  horizontal right: horizontal right
  horizontal center: horizontal center
  layout: Layout
  columns: Columns
  rows: Rows
  loosen up gallery: Loosen up gallery
  alignment: Align content
  margin: Margin
</i18n>

<i18n lang="yaml" locale="de">
  standard: Standard
  justified: Galeriemodus
  column: "Keine Spalten | Eine Spalte | {no} Spalten"
  row: "keine Zeilen | eine Zeile | {no} Zeilen"
  auto: auto
  small: klein
  medium: mittel
  large: groß
  off: aus
  every n rows: "alle {no} Zeilen"
  full width: Breite ausfüllen
  full height: Höhe ausfüllen
  horizontal left: Horizontal links
  horizontal right: Horizontal rechts
  horizontal center: Horizontal zentriert
  layout: Layout
  columns: Spalten
  rows: Zeilen
  loosen up gallery: Galerie auflockern
  alignment: Inhalt ausrichten
  margin: Abstand
</i18n>

<template>
  <div
    :class="{fullscreen: fullscreen}"
  >
    <component
      v-if="!fullscreen"
      :is="$editMode && !$localeMode ? 'files-editor' : 'div'"
      :class="containerClasses"
    >
      <component
        :is="layout"
        :rows-justified="rowsJustified"
        :row-cadence="rowCadence"
        :container-width="boxWidth"
      >
        <component
          ref="children"
          v-for="file in files"
          :is="file.type"
          :key="file.path"
          :path="file.path"
          v-bind="file.props"
        />
      </component>
    </component>
    <layout-fullscreen
      v-else
      :class="containerClasses"
    >
      <component
        ref="children"
        v-for="file in files"
        :is="file.type"
        :key="file.path"
        :path="file.path"
        v-bind="file.props"
        :is-fullscreen="true"
      />
    </layout-fullscreen>
  </div>
</template>

<script>
import FilesEditor from '@/components/editor/FilesEditor'
import BaseModule from '@/components/modules/BaseModule'
import LayoutStandard from '@/components/modules/Files/layouts/LayoutStandard'
import LayoutJustified from '@/components/modules/Files/layouts/LayoutJustified'
import LayoutFullscreen from '@/components/modules/Files/layouts/LayoutFullscreen'
import translatable from '@/store/mixins/translatable'

export default {
  // eslint-disable-next-line vue/name-property-casing
  name: 'file',
  extends: BaseModule,
  customStore: translatable,
  components: {
    FilesEditor,
    LayoutStandard,
    LayoutJustified,
    LayoutFullscreen
  },
  props: {
    // eslint-disable-next-line
    _headlineLayout: {
      input: 'heading',
      label: 'layout'
    },
    layout: {
      type: String,
      input: 'radio',
      choices: {
        'layout-standard': 'standard',
        'layout-justified': 'justified'
      },
      default: 'layout-standard',
      validator (value) {
        return ['layout-standard', 'layout-justified'].includes(value)
      }
    },
    // eslint-disable-next-line
    _headlineColumns: {
      input: 'heading',
      condition: values => values.layout === 'layout-standard',
      label: 'columns'
    },
    columns: {
      type: String,
      input: 'radio',
      condition: values => values.layout === 'layout-standard',
      choices: {
        'col-1': ['tc', 'column', 1, { no: 1 }],
        'col-2': ['tc', 'column', 2, { no: 2 }],
        'col-3': ['tc', 'column', 3, { no: 3 }],
        'col-4': ['tc', 'column', 4, { no: 4 }],
        'col-5': ['tc', 'column', 5, { no: 5 }],
        'col-6': ['tc', 'column', 6, { no: 6 }]
      },
      default: 'col-1',
      validator (value) {
        return ['col-1', 'col-2', 'col-3', 'col-4', 'col-5', 'col-6'].includes(value)
      }
    },
    // eslint-disable-next-line
    _headlineRows: {
      input: 'heading',
      label: 'rows'
    },
    rows: {
      type: String,
      input: 'radio',
      condition: values => values.layout === 'layout-standard',
      choices: {
        'row-1': ['tc', 'row', 1, { no: 1 }],
        'row-2': ['tc', 'row', 2, { no: 2 }],
        'row-3': ['tc', 'row', 3, { no: 3 }],
        'row-4': ['tc', 'row', 4, { no: 4 }],
        'row-5': ['tc', 'row', 5, { no: 5 }],
        'row-6': ['tc', 'row', 6, { no: 6 }],
        'row-auto': 'auto'
      },
      default: 'row-auto',
      validator (value) {
        return ['row-1', 'row-2', 'row-3', 'row-4', 'row-5', 'row-6', 'row-auto'].includes(value)
      }
    },
    rowsJustified: {
      type: Number,
      input: 'radio',
      condition: values => values.layout === 'layout-justified',
      choices: {
        150: 'small',
        300: 'medium',
        450: 'large'
      },
      default: 300,
      validator (value) {
        return [150, 300, 450].includes(value)
      }
    },
    // eslint-disable-next-line
    _headlineRowCadence: {
      input: 'heading',
      condition: values => values.layout === 'layout-justified',
      label: 'loosen up gallery'
    },
    rowCadence: {
      type: Number,
      input: 'radio',
      condition: values => values.layout === 'layout-justified',
      choices: {
        0: 'off',
        3: ['t', 'every n rows', { no: 3 }],
        6: ['t', 'every n rows', { no: 6 }]
      },
      default: 0,
      validator (value) {
        return [0, 3, 6].includes(value)
      }
    },
    // eslint-disable-next-line
    _headlineAlignment: {
      input: 'heading',
      condition: values => values.layout === 'layout-standard',
      label: 'alignment'
    },
    clipping: {
      type: String,
      input: 'radio',
      condition: values => values.layout === 'layout-standard',
      choices: {
        'width-100': 'full width',
        'height-100': 'full height'
      },
      default: 'width-100',
      validator (value) {
        return ['width-100', 'height-100'].includes(value)
      }
    },
    alignment: {
      type: String,
      input: 'radio',
      condition: values => values.layout === 'layout-standard',
      choices: {
        'align-center': 'horizontal center',
        'align-left': 'horizontal left',
        'align-right': 'horizontal right'
      },
      default: 'align-center',
      validator (value) {
        return ['align-center', 'align-left', 'align-right'].includes(value)
      }
    },
    margin: {
      label: 'margin',
      input: 'checkbox',
      condition: values => values.layout === 'layout-standard',
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      dragging: false
    }
  },
  inject: ['$cache', '$box'],
  computed: {
    boxWidth () {
      return this.$box.boxWidth
    },
    containerClasses () {
      return {
        container: true,
        [this.layout]: true,
        [this.columns]: true,
        [this.rows]: true,
        [this.clipping]: true,
        [this.alignment]: true,
        margin: this.margin
      }
    },
    cache () {
      return this.$cache
    },
    files () {
      return this.children.map(f => {
        return {
          type: f.getters.type,
          path: f.getters.path,
          props: f.getters.props
        }
      })
    },
    fullscreen () {
      return !this.$editMode && this.isActive && Boolean(this.$route.query.fullscreen)
    }
  },
  updated () {
    // TODO: Check if we need this
    // force update children
    if (this.files.length > 0) {
      for (const child of this.$refs.children) {
        child.$forceUpdate()
      }
    }
  }
}
</script>

<style scoped lang="scss">
$space: 10px;

.sortable-item {
  cursor: pointer;
  &:active {
    cursor: grabbing;
  }
  &.drop-placeholder {
    background: rgba(0, 0, 0, 0.4);
    border: 1px dashed color(secondary);
  }
}

.content:not(.fullscreen) .container.layout-standard {
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
  height: 100%;

  /deep/ > .file {
    overflow: hidden;
    display: flex;
    align-items: center;
    max-height: 100%;
  }

  &.col-1 /deep/ > .file {
    width: 100%;
  }

  &.col-2 /deep/ > .file {
    width: 50%;
  }

  &.col-3 /deep/ > .file {
    width: 33.33%;
  }

  &.col-4 /deep/ > .file {
    width: 25%;
  }

  &.col-5 /deep/ > .file {
    width: 20%;
  }

  &.col-6 /deep/ > .file {
    width: 16.6667%;
  }

  &.row-1 /deep/ > .file {
    height: 100%;
  }

  &.row-2 /deep/ > .file {
    height: 50%;
  }

  &.row-3 /deep/ > .file {
    height: 33.33%;
  }

  &.row-4 /deep/ > .file {
    height: 25%;
  }

  &.row-5 /deep/ > .file {
    height: 20%;
  }

  &.row-6 /deep/ > .file {
    height: 16.6667%;
  }

  &.row-auto /deep/ > .file {
    height: auto;
  }

  &.margin {
    margin: (-$space / 2) (-$space / 2) 0;

    /deep/ > .file {
      margin: $space / 2;
    }

    &.col-2 /deep/ > .file {
      width: calc(50% - #{$space});
    }

    &.col-3 /deep/ > .file {
      width: calc(33.33% - #{$space});
    }

    &.col-4 /deep/ > .file {
      width: calc(25% - #{$space});
    }

    &.col-5 /deep/ > .file {
      width: calc(20% - #{$space});
    }

    &.col-6 /deep/ > .file {
      width: calc(16.6667% - #{$space});
    }

    &.row-2 /deep/ > .file {
      height: calc(50% - #{$space});
    }

    &.row-3 /deep/ > .file {
      height: calc(33.33% - #{$space});
    }

    &.row-4 /deep/ > .file {
      height: calc(25% - #{$space});
    }

    &.row-5 /deep/ > .file {
      height: calc(20% - #{$space});
    }

    &.row-6 /deep/ > .file {
      height: calc(16.6667% - #{$space});
    }
  }

  &.width-100 /deep/ > .file > .content {
    width: 100%;
    height: auto;
  }

  &.height-100 /deep/ > .file > .content {
    width: auto;
    height: 100%;
  }

  &.height-100 /deep/ > .video > .content {
    object-fit: cover;
  }

  &.align-center /deep/ > .file {
    justify-content: center;
  }

  &.align-left /deep/ > .file {
    justify-content: flex-start;
  }

  &.align-right /deep/ > .file {
    justify-content: flex-end;
  }
}

.content.fullscreen {
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: z(modal);
  background: #000 !important;
  margin: 0;
  animation-duration: 0.75s;
  animation-fill-mode: both;
  animation-name: bounceInDown;

  .container {
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;

    /deep/ > .file {
      position: absolute;
      left: 0;
      top: 0;
      right: 0;
      bottom: 0;
      transition: transform 200ms ease-in;

      &.panning {
        cursor: grab;
        transition: none;
      }

      .caption {
        position: fixed;
        bottom: 10px;
        left: 50%;
        transform: translate(-50%, 0);
        background: rgba(0, 0, 0, 0.7);
        padding: 1.3rem 2rem;
        font-size: 1.3rem;
        border-radius: 1rem;
        color: white;
      }
    }
  }
}
</style>
